import { useDocumentTitle } from '../../common/useDocumentTitle'
import { Col, Container, Form, InputGroup, Row, Spinner, Stack } from 'react-bootstrap'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { SessionUserFilter } from '../../components/SessionFilters/SessionUserFilter'
import { Divider } from '../../components/Divider/Divider'
import { SessionContactFilter } from '../../components/SessionFilters/SessionContactFilter'
import { LiveHeaderRow } from './LiveHeaderRow'
import { resetSessionFilter, setSessionFilterContactIds, setSessionFilterUserIds } from '../../store/sessionFilter/sessionFilter'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { useSearchParams } from 'react-router-dom'
import { RoomSession } from '../../models/Room'
import { LiveMeetingListItem } from './LiveMeetingListItem'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import { generateId, parseMetadata } from '../../common/util'
import { LiveMeetingsPoller } from './LiveMeetingsPoller'
import {
  selectLiveMeetings,
  selectLiveMeetingsIsLoading,
  setHasLiveMeeting,
  startLiveMeetingsPolling,
  stopLiveMeetingsPolling,
} from '../../store/liveMeetings/liveMeetings'
import { LiveMeetingsVideo } from './LiveMeetingsVideo'
import { MeetingProviderAccountIntegrationConfig } from '../../models/Account'
import { useGetAccountQuery } from '../../store/accountUserSettings/accountUserSettings'
import { ManageCallModal } from '../Upcoming/ManageCallModal'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { supportedCallSolutions } from '../../common/integrations'
import { CollapsiblePanel } from '../../components/CollapsiblePanel/CollapsiblePanel'
import { isLiveSession } from '../../common/session.util'

export const LiveMeetings: React.FC = () => {
  useDocumentTitle('Live Meetings')
  const dispatch = useAppDispatch()
  const { hasQuota } = useZocksQuota()
  const sessionSharingAvailable = hasQuota('identity.sessionSharingEnabled.value')

  const [searchParams] = useSearchParams()
  const userId = searchParams.get('userId')

  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const { data: account } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const [liveMeetingPollerKey, setLiveMeetingPollerKey] = useState(generateId())
  const phoneConfig = useMemo(() => {
    if (!account?.data?.effectiveConfig?.integrations) {
      return null
    }
    return (
      (account.data.effectiveConfig.integrations.find(
        (integration) => integration.type === 'meeting_provider' && integration.serviceId === 'phone',
      ) as MeetingProviderAccountIntegrationConfig) ?? null
    )
  }, [account])
  const liveMeetings = useAppSelector(selectLiveMeetings)
  const isLoading = useAppSelector(selectLiveMeetingsIsLoading)

  useEffect(() => {
    if (!userId) {
      dispatch(setSessionFilterUserIds(sessionSharingAvailable ? ['{all}'] : []))
    }
    dispatch(startLiveMeetingsPolling())
    return () => {
      dispatch(stopLiveMeetingsPolling())
    }
  }, [dispatch, sessionSharingAvailable, userId])

  const sortedSessions = useMemo(
    () =>
      [...(liveMeetings ?? [])].sort(
        (s0, s1) =>
          Number(s1.isActive) - Number(s0.isActive) || s0.startedAt.localeCompare(s1.startedAt) || (s0.finishedAt ?? '').localeCompare(s1.finishedAt ?? ''),
      ),
    [liveMeetings],
  )

  useEffect(() => {
    if (isLoading) {
      return
    }

    const ownLiveMeetings = sortedSessions?.filter((s) => s.roomCreatedBy?.id === currentUserId && isLiveSession(s))
    dispatch(setHasLiveMeeting(!!ownLiveMeetings?.length))
  }, [currentUserId, dispatch, isLoading, liveMeetings, sortedSessions])

  const ownSessions = sortedSessions?.filter((s) => s.roomCreatedBy?.id === currentUserId)
  const teamSessions = sortedSessions?.filter((s) => s.roomCreatedBy?.id !== currentUserId)

  return (
    <>
      <LiveMeetingsPoller key={liveMeetingPollerKey} />
      <Container className='px-0'>
        <LiveMeetingsVideo />
        <Stack gap={3}>
          <Row>
            <Col>
              <h3>Live Meetings</h3>
            </Col>
          </Row>
          <Stack direction='vertical' gap={5}>
            <OwnLiveMeetingList isLoading={isLoading && !ownSessions?.length} roomSessions={ownSessions ?? []} phoneConfig={phoneConfig} />
            <LiveHeaderRow />
            {sessionSharingAvailable ? (
              <LiveMeetingList
                onSessionChange={() => setLiveMeetingPollerKey(generateId())}
                isLoading={isLoading && !teamSessions?.length}
                roomSessions={teamSessions ?? []}
              />
            ) : null}
          </Stack>
        </Stack>
      </Container>
    </>
  )
}

export interface OwnLiveMeetingListProps {
  isLoading: boolean
  roomSessions: RoomSession[]
  phoneConfig: MeetingProviderAccountIntegrationConfig | null
}

const OwnLiveMeetingList: React.FC<OwnLiveMeetingListProps> = ({ isLoading, roomSessions, phoneConfig }) => {
  const [ownRoomSessions, setOwnRoomSessions] = useState<RoomSession[]>(roomSessions)

  useEffect(() => {
    setOwnRoomSessions(roomSessions)
  }, [roomSessions])

  const onSessionChange = useCallback((roomSession: RoomSession) => {
    setOwnRoomSessions((currentOwnRoomSessions) => currentOwnRoomSessions.map((s) => (s.id === roomSession.id ? roomSession : s)))
  }, [])

  return (
    <Container className='p-0'>
      {isLoading ? (
        <Row className='mt-3'>
          <Col className='d-flex justify-content-center'>
            <Spinner />
          </Col>
        </Row>
      ) : ownRoomSessions.length === 0 ? (
        <div className='paragraph-large'>You don’t have any live meetings right now.</div>
      ) : (
        <Row>
          <Col>
            <RoomSessionList roomSessions={ownRoomSessions} onSessionChange={onSessionChange} phoneConfig={phoneConfig} />
          </Col>
        </Row>
      )}
    </Container>
  )
}

const isFilterMatch = (source: { [key: string]: unknown }, property: string, filter: string) => {
  return (property in source && typeof source[property] === 'string' && (source[property] as string).toLowerCase().includes(filter.toLowerCase())) || false
}

export interface LiveMeetingListProps {
  isLoading: boolean
  roomSessions: RoomSession[]
  onSessionChange: () => void
}

const LiveMeetingList: React.FC<LiveMeetingListProps> = ({ isLoading, roomSessions, onSessionChange }) => {
  const dispatch = useAppDispatch()
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const defaultUserIds = useMemo(() => ['{all}'], [])
  const [searchParams, setSearchParams] = useSearchParams()
  const [roomSessionTextFilter, setRoomSessionTextFilter] = useState<string>('')

  useEffect(() => {
    const [userId, contactId] = [searchParams.get('userId'), searchParams.get('contactId')]
    if (userId) {
      dispatch(setSessionFilterUserIds(userId.split(',')))
    } else {
      dispatch(setSessionFilterUserIds(['{all}']))
    }
    if (contactId) {
      dispatch(setSessionFilterContactIds(contactId.split(',')))
    }
  }, [dispatch, searchParams])

  const filterRoomSessions = useCallback((sessions: RoomSession[], filterInput: string): RoomSession[] => {
    if (!filterInput) {
      return sessions
    }
    const filter = filterInput.toLowerCase()
    return sessions.filter((session) => {
      const sessionMetadata = parseMetadata(session.metadata)
      const externalSessionMetadata: { [key: string]: string } = sessionMetadata?.external ?? {}

      if (isFilterMatch(externalSessionMetadata, 'title', filter) || isFilterMatch(sessionMetadata, 'source', filter)) {
        return true
      }

      return !!session.users.filter((user) => {
        return (
          user.id?.toLowerCase().includes(filter) ||
          user.name?.toLowerCase().includes(filter) ||
          user.email?.toLowerCase().includes(filter) ||
          user.firstName?.toLowerCase().includes(filter) ||
          user.lastName?.toLowerCase().includes(filter) ||
          user.username?.toLowerCase().includes(filter) ||
          user.phoneNumber?.toLowerCase().includes(filter)
        )
      }).length
    })
  }, [])

  const onRoomSessionTextFilterChange = (filter: string) => {
    setRoomSessionTextFilter(filter)
  }

  const resetFilter = () => {
    dispatch(resetSessionFilter(defaultUserIds))
    onRoomSessionTextFilterChange('')
    setSearchParams((params) => {
      params.delete('userId')
      params.delete('contactId')
      return params
    })
  }

  const onFilterChange = (searchParams: URLSearchParams) => {
    if (!searchParams.get('userId') && !searchParams.get('filterId')) {
      searchParams.set('userId', currentUserId)
      dispatch(setSessionFilterUserIds(defaultUserIds))
    }
    setSearchParams(searchParams, { replace: true })
  }

  const filteredRoomSessions = useMemo(() => filterRoomSessions(roomSessions, roomSessionTextFilter), [filterRoomSessions, roomSessionTextFilter, roomSessions])

  return (
    <CollapsiblePanel
      id='LiveMeetings-TeamMembersLiveMeetings'
      header={<h4>Your Team's Live Meetings</h4>}
      defaultCollapsedState='expanded'
      shouldStoreCollapsedState
      hideBorderWhileExpanded
    >
      <Stack gap={4}>
        <Row className='align-items-end'>
          <Col className='d-none d-lg-block'>
            <SessionUserFilter filterType='live' disabled={isLoading} onFilterChanged={onFilterChange} />
          </Col>
          <Col className='d-none d-lg-block'>
            <SessionContactFilter filterType='live' disabled={isLoading} onFilterChanged={onFilterChange} />
          </Col>
          <Col xs={12} sm='auto' lg className='mt-3 mt-sm-0'>
            <div className='d-flex zocks-gap-0.75 align-items-center justify-content-end'>
              <div className='d-none d-lg-flex gap-1 cursor-pointer' onClick={resetFilter}>
                <i className='bi bi-arrow-clockwise' />
                <div className='button-link-medium'>Clear all</div>
              </div>
            </div>
          </Col>
        </Row>
        <Divider />
        {isLoading ? (
          <Row className='mt-3'>
            <Col className='d-flex justify-content-center'>
              <Spinner />
            </Col>
          </Row>
        ) : (
          <>
            <Row>
              <Col>
                <div>
                  {filteredRoomSessions.length} {`meeting${filteredRoomSessions.length !== 1 ? 's' : ''}`}
                </div>
              </Col>
              <Col xs={12} md='auto' className='m-0'>
                <InputGroup className='input-group-container gap-4'>
                  <Form.Control
                    type='text'
                    list='rooms'
                    value={roomSessionTextFilter}
                    onChange={(e) => onRoomSessionTextFilterChange(e.currentTarget.value)}
                    placeholder='Search in meetings'
                  />
                  <i className='px-2 bi bi-search'></i>
                </InputGroup>
              </Col>
            </Row>
            <Row>
              <Col>
                <RoomSessionList roomSessions={filteredRoomSessions} onSessionChange={onSessionChange} phoneConfig={null} />
              </Col>
            </Row>
          </>
        )}
      </Stack>
    </CollapsiblePanel>
  )
}

interface RoomSessionListProps {
  roomSessions: RoomSession[]
  onSessionChange?: (roomSession: RoomSession) => void
  phoneConfig: MeetingProviderAccountIntegrationConfig | null
}

const RoomSessionList: React.FC<RoomSessionListProps> = ({ roomSessions, onSessionChange, phoneConfig }) => {
  const { tracked } = useTracking()
  const [showManageCallModal, setShowManageCallModal] = useState(false)
  const [selectedSession, setSelectedSession] = useState<RoomSession | null>(null)
  const [doCall, setDoCall] = useState(false)
  const provider = useMemo(() => {
    return supportedCallSolutions.find((integration) => integration.id === phoneConfig?.defaultProvider) ?? null
  }, [phoneConfig])

  const onSessionSelectedForCall = tracked(
    ({ session, doCall }: { session: RoomSession; doCall: boolean }) => {
      setDoCall(doCall)
      setSelectedSession(session)
      setShowManageCallModal(true)
    },
    { eventDetails: { provider: { id: provider?.id ?? null, displayName: provider?.displayName ?? 'No Provider' } } },
    { eventNameExtractor: ({ doCall }) => (doCall ? TrackEvent.PHONE_START_CALL : TrackEvent.PHONE_ADD_TO_ONGOING_CALL) },
  )

  const onModalHide = () => {
    setShowManageCallModal(false)
    setSelectedSession(null)
  }

  return (
    <Container fluid className='p-0'>
      {showManageCallModal && phoneConfig && selectedSession && (
        <ManageCallModal show={showManageCallModal} session={selectedSession} phoneConfig={phoneConfig} doCall={doCall} onHide={onModalHide} />
      )}
      <Stack gap={3}>
        {roomSessions.map((roomSession) => {
          return (
            <LiveMeetingListItem
              key={roomSession.id}
              session={roomSession}
              onSessionChange={onSessionChange}
              phoneConfig={phoneConfig}
              onSessionSelectedForCall={onSessionSelectedForCall}
            />
          )
        })}
      </Stack>
    </Container>
  )
}
